// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

// Functional core for drawing a feedback loop as a closed cycle (MVP WS2,
// AC2.2). Given the loop's member positions in loop order, produce the SVG path
// that traces the cycle and closes back to the first member -- so hovering /
// pinning a loop makes it "come alive" as a closed path, not just per-node
// emphasis. Pure, so it unit-tests without a live canvas.

export interface Pt {
  readonly x: number;
  readonly y: number;
}

/**
 * SVG path string for a loop's closed cycle through its member positions, in
 * loop order, closing back to the first member.
 *
 * - Two members render as a lens (two opposed quadratic curves) so the cycle
 *   still reads when a straight there-and-back would collapse to a line (e.g. a
 *   two-variable balancing loop).
 * - Three or more members render as a closed polygon through the members in
 *   order -- literally the feedback path.
 * - Fewer than two members yields '' (nothing meaningful to draw).
 */
export function loopCyclePath(points: readonly Pt[]): string {
  if (points.length < 2) {
    return '';
  }
  if (points.length === 2) {
    const [a, b] = points;
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    // Perpendicular unit vector; the two control points bulge the arcs to
    // opposite sides of the a-b line to form a lens.
    const nx = -dy / len;
    const ny = dx / len;
    const bulge = len * 0.35;
    return (
      `M${a.x},${a.y}` +
      `Q${mx + nx * bulge},${my + ny * bulge} ${b.x},${b.y}` +
      `Q${mx - nx * bulge},${my - ny * bulge} ${a.x},${a.y}` +
      `Z`
    );
  }
  const [first, ...rest] = points;
  let d = `M${first.x},${first.y}`;
  for (const p of rest) {
    d += `L${p.x},${p.y}`;
  }
  return d + 'Z';
}

/**
 * SVG path for a loop's closed cycle drawn as smooth arcs on its fitted RING,
 * rather than straight chords (`loopCyclePath`). Used for the highlighted-loop
 * overlay so it follows the same continuous ring the causal-link arcs trace
 * (Williams 2026-08-11 dynamic tangent-attachment reference), instead of a
 * polygon jutting inside them.
 *
 * The CLD layout seats a loop's members on a circle, so the ring is recovered as
 * their centroid + mean radius; each consecutive pair is joined by the minor arc
 * on that circle (the segment that hugs the ring between adjacent members). Two
 * members fall back to the `loopCyclePath` lens; fewer than two yield ''.
 */
export function loopRingPath(points: readonly Pt[]): string {
  if (points.length < 3) {
    return loopCyclePath(points);
  }
  let cx = 0;
  let cy = 0;
  for (const p of points) {
    cx += p.x;
    cy += p.y;
  }
  cx /= points.length;
  cy /= points.length;
  let r = 0;
  for (const p of points) {
    r += Math.hypot(p.x - cx, p.y - cy);
  }
  r /= points.length;
  if (r < 1e-6) {
    return loopCyclePath(points);
  }

  let d = `M${points[0].x},${points[0].y}`;
  for (let i = 1; i <= points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i % points.length];
    // Sweep flag selecting the minor arc that curves around the centroid: sign of
    // (prev-C) x (curr-C) in screen space (y down) tells us whether `curr` sits
    // clockwise from `prev` around C, which is exactly SVG's positive-sweep sense.
    const cross = (prev.x - cx) * (curr.y - cy) - (prev.y - cy) * (curr.x - cx);
    const sweep = cross > 0 ? 1 : 0;
    d += `A${r},${r} 0 0 ${sweep} ${curr.x},${curr.y}`;
  }
  return d + 'Z';
}
